/**
 * Goals Redux Slice
 * Uses the simpler Goal structure from achievements.ts
 * Matches the original Marune app's goal system
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Goal, GoalProgress } from '../../types/achievements';
import goalsService from '../../services/goals.service';
import { RootState } from '../index';

interface GoalsState {
  goals: Goal[];
  progress: GoalProgress[];
  loading: boolean;
  error: string | null;
}

const initialState: GoalsState = {
  goals: [],
  progress: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchUserGoals = createAsyncThunk(
  'goals/fetchUserGoals',
  async (uid: string) => {
    const goals = await goalsService.getUserGoals(uid);
    const progress = await goalsService.getCurrentGoalProgress(uid);
    return { goals, progress };
  }
);

export const createGoal = createAsyncThunk(
  'goals/createGoal',
  async ({ uid, goal }: { uid: string; goal: Goal }) => {
    const goalId = await goalsService.saveGoal(uid, goal);
    return { ...goal, id: goalId };
  }
);

export const updateGoal = createAsyncThunk(
  'goals/updateGoal',
  async ({ uid, goal }: { uid: string; goal: Goal }) => {
    await goalsService.saveGoal(uid, goal);
    return goal;
  }
);

export const toggleGoalActive = createAsyncThunk(
  'goals/toggleActive',
  async ({ uid, goalId }: { uid: string; goalId: string }) => {
    await goalsService.toggleGoalActive(uid, goalId);
    const goals = await goalsService.getUserGoals(uid);
    return goals;
  }
);

export const deleteGoal = createAsyncThunk(
  'goals/deleteGoal',
  async ({ uid, goalId }: { uid: string; goalId: string }) => {
    await goalsService.deleteGoal(uid, goalId);
    return goalId;
  }
);

// Slice
const goalsSlice = createSlice({
  name: 'goals',
  initialState,
  reducers: {
    setGoals: (state, action: PayloadAction<Goal[]>) => {
      state.goals = action.payload;
    },
    setProgress: (state, action: PayloadAction<GoalProgress[]>) => {
      state.progress = action.payload;
    },
    clearGoals: (state) => {
      state.goals = [];
      state.progress = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch goals
      .addCase(fetchUserGoals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserGoals.fulfilled, (state, action) => {
        state.loading = false;
        state.goals = action.payload.goals;
        state.progress = action.payload.progress;
      })
      .addCase(fetchUserGoals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch goals';
      })
      // Create goal
      .addCase(createGoal.fulfilled, (state, action) => {
        state.goals.push(action.payload);
      })
      // Update goal
      .addCase(updateGoal.fulfilled, (state, action) => {
        const index = state.goals.findIndex(g => g.id === action.payload.id);
        if (index !== -1) {
          state.goals[index] = action.payload;
        }
      })
      // Toggle active
      .addCase(toggleGoalActive.fulfilled, (state, action) => {
        state.goals = action.payload;
      })
      // Delete goal
      .addCase(deleteGoal.fulfilled, (state, action) => {
        state.goals = state.goals.filter(g => g.id !== action.payload);
        state.progress = state.progress.filter(p => p.goalId !== action.payload);
      });
  },
});

// Actions
export const { setGoals, setProgress, clearGoals } = goalsSlice.actions;

// Selectors
export const selectGoals = (state: RootState) => state.goals.goals;
export const selectGoalsProgress = (state: RootState) => state.goals.progress;
export const selectGoalsLoading = (state: RootState) => state.goals.loading;
export const selectGoalsError = (state: RootState) => state.goals.error;

export const selectActiveGoals = (state: RootState) => 
  state.goals.goals.filter(g => g.isActive);

export const selectGoalProgress = (goalId: string) => (state: RootState) =>
  state.goals.progress.find(p => p.goalId === goalId);

export default goalsSlice.reducer;